import { FC, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Plus, Zap, Loader2 } from 'lucide-react';
import { Select } from '@crisellevaldez/speculo-ui';
import { formatNumber } from '../../utils/format';

import { useDashboard } from '../../hooks/dashboard/useDashboard';
import { useHandoffStats } from '../../hooks/dashboard/handoff';
import { useOrgLiveTransfersGQL } from '../../hooks/graphql/dashboard/useOrgLiveTransfersGQL';
import { useOrgCallsGQL } from '../../hooks/graphql/dashboard/useOrgCallsGQL';
import { useOrganizationStore, useFlywheelEnabled } from '../../stores/useOrganizationStore';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import {
  handleDateRangeChange,
  getDefaultDateRangeFromJune2025,
} from '../../utils/dateRangeTimezone';
import { useWidgetPreferences, getDefaultOrder } from '../../stores/useWidgetPreferences';
import { usePinnedWidgets } from '../../stores/usePinnedWidgets';
import { useLivePinnedWidgets } from '../../hooks/useLiveBlock';
import { SortableStatCard } from './SortableStatCard';
import { AddWidgetCard, AvailableWidget } from './AddWidgetCard';
import { DateRangeBar } from './DateRangeBar';
import { StatsModal } from '../features/dashboard/StatsModal';
import { CallStatsModal, GQLCallSummary } from '../features/contacts/CallStatsModal';
import { HandoffsStatsModal } from '../features/dashboard/HandoffsStatsModal';
import { LiveTransfersModal } from '../features/contacts/LiveTransfersModal';
import { SmsThreadModal } from '../features/dashboard/SmsThreadModal';
import { WebActivityStatsModal } from '../features/dashboard/modals/WebActivityStatsModal';
import { EmailsStatsModal } from '../features/dashboard/modals/EmailsStatsModal';
import { EmailEngagementStatsModal } from '../features/dashboard/modals/EmailEngagementStatsModal';
import { SmsStatsModal } from '../features/dashboard/modals/SmsStatsModal';
import { EditContactModal } from '../features/contacts/EditContactModal';
import { useFollowUpStats } from '../../hooks/contacts/useFollowUpDecisions';
import {
  EMPTY_FOLLOW_UP_STRATEGY,
  getFollowUpTypeBreakdown,
  sumFollowUpTypes,
} from '../../api/nextFollowUp';
import { formatCallDuration } from '../../utils/duration';
import { LeadActivity } from './LeadActivity';
import { useConversationThreadsGQL } from '../../hooks/graphql/dashboard/useConversationThreadsGQL';
import { SmartPointerSensor } from './SmartPointerSensor';
import {
  ALL_WIDGETS,
  buildWidgetMap,
  buildPinnedWidgetMap,
  FlywheelDrilldownFilter,
  FollowUpData,
} from './widgetDefinitions';
import { getLeadsColumns } from './dashboardColumns';
import {
  FlywheelContactsModal,
  FlywheelFollowUpsModal,
} from '../features/dashboard/FlywheelFollowUpsModal';

export const DashboardStats: FC = () => {
  const organization = useOrganizationStore((state) => state.organization);
  const timezone = organization?.timezone || 'UTC';
  const flywheelEnabled = useFlywheelEnabled();
  const {
    widgetOrder,
    setWidgetOrder,
    removeWidget,
    getWidgetSize,
    setWidgetSize,
    isHighlighted,
    toggleHighlight,
    isCustomized,
    syncFromAPI,
    isSyncing,
  } = useWidgetPreferences();
  const { widgets: rawPinnedWidgets, unpinWidget } = usePinnedWidgets();
  const pinnedWidgets = useLivePinnedWidgets(rawPinnedWidgets);

  useEffect(() => {
    if (organization?.id) syncFromAPI(organization.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>(() =>
    getDefaultDateRangeFromJune2025(timezone)
  );

  // Widget library state
  const [showLibrary, setShowLibrary] = useState(false);

  // Modal state
  const [showLeads, setShowLeads] = useState(false);
  const ALL_LEAD_SOURCES = ['Email', 'PPC', 'Organic'];
  const [leadsSourceFilter, setLeadsSourceFilter] = useState<string[]>(ALL_LEAD_SOURCES);
  // Debounced backend search for the Leads modal — typed text is buffered by
  // StatsModal's internal input state; we just need to delay the GQL refetch.
  const [leadsSearchDebounced, setLeadsSearchDebounced] = useState('');
  const leadsSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleLeadsSearch = (val: string) => {
    if (leadsSearchTimerRef.current) clearTimeout(leadsSearchTimerRef.current);
    leadsSearchTimerRef.current = setTimeout(() => setLeadsSearchDebounced(val), 400);
  };
  const [showOpportunities, setShowOpportunities] = useState(false);
  const [showCalls, setShowCalls] = useState(false);
  const [showConversations, setShowConversations] = useState(false);
  // One discriminated state instead of a boolean per bucket. `null` = closed;
  // any status string opens the single HandoffsStatsModal scoped to that
  // bucket. Statuses track the SPEC-2300 state machine.
  type HandoffTile = 'pending' | 'claimed' | 'released' | 'declined' | 'expired';
  const [handoffStatus, setHandoffStatus] = useState<HandoffTile | null>(null);
  const [showLiveTransfers, setShowLiveTransfers] = useState(false);
  const [showEmailOpens, setShowEmailOpens] = useState(false);
  const [showSms, setShowSms] = useState(false);
  // Drill-down from the SMS modal row into a single contact's thread.
  // Row click still opens the contact edit modal — this opens just the
  // chat surface (same SmsThreadModal used by Recent Activities).
  // The row carries `matchedMessageId` (newest match in the active date
  // range, else thread latest) as the anchor — SmsThreadModal opens
  // scrolled to that bubble via PR #2423's before/after cursors.
  const [selectedSmsThread, setSelectedSmsThread] = useState<{
    contactId: string;
    contactName: string | null;
    contactPhone: string | null;
    totalCount: number | null;
    anchor: {
      messageId: string;
      body: string | null;
      direction: string | null;
      sentAt: string | null;
    } | null;
  } | null>(null);
  const [showWebsiteStats, setShowWebsiteStats] = useState(false);
  const [showPageViews, setShowPageViews] = useState(false);
  const [showEmailEngagement, setShowEmailEngagement] = useState<'open' | 'click' | null>(null);
  const [showOpportunityCalls, setShowOpportunityCalls] = useState(false);
  const [flywheelDrilldown, setFlywheelDrilldown] = useState<FlywheelDrilldownFilter | null>(null);
  // TODO(migration): webGuestFilter disabled — GQL webActivities has no isGuest filter param.
  //   Backend needs: webActivities(isGuest: Boolean) to support Logged In / Guest / eAlert filtering.
  // const [webGuestFilter, setWebGuestFilter] = useState<'all' | 'guest' | 'guest-ealert' | 'logged-in'>('all');
  // TODO(migration): webDeviceFilter disabled — GQL webActivities has no deviceType filter param.
  //   Backend needs: webActivities(deviceType: String) to support Desktop / Mobile / Tablet filtering.
  // const [webDeviceFilter, setWebDeviceFilter] = useState('all');
  const [webEventTypeFilter, setWebEventTypeFilter] = useState('all');
  // Website Interactions modal — device + visitor + debounced search.
  const [webDeviceFilter, setWebDeviceFilter] = useState('all');
  const [webVisitorFilter, setWebVisitorFilter] = useState<'all' | 'identified' | 'guest'>('all');
  const [webSearchDebounced, setWebSearchDebounced] = useState('');
  const webSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleWebSearch = (val: string) => {
    if (webSearchTimerRef.current) clearTimeout(webSearchTimerRef.current);
    webSearchTimerRef.current = setTimeout(() => setWebSearchDebounced(val), 400);
  };
  const [leadsSortField, setLeadsSortField] = useState<string>('dateTime');
  const [leadsSortDirection, setLeadsSortDirection] = useState<'asc' | 'desc'>('desc');
  const [leadsPageSize, setLeadsPageSize] = useState(25);
  const [selectedContact, setSelectedContact] = useState<{ id: string } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const onDateRangeChange = useCallback(
    (newRange: { from: Date | null; to: Date | null }) => {
      const result = handleDateRangeChange(newRange, timezone, () =>
        getDefaultDateRangeFromJune2025(timezone)
      );
      setDateRange(result.dateRange);
    },
    [timezone]
  );

  // Convert UI filter value to GQL eventTypes array — exclude page_view and custom by default
  const webEventTypes =
    webEventTypeFilter && webEventTypeFilter !== 'all'
      ? [webEventTypeFilter]
      : ['property_view', 'search', 'lead_capture', 'property_saved'];

  const {
    data: dashboardData,
    isLoading,
    refreshLeads,
    totalLeadsCount,
    leadsCurrentPage,
    goToLeadsPage,
    calls: callsGQL,
    emails: emailsGQL,
    emailEngagement: emailEngagementGQL,
    webActivities: webActivitiesGQL,
  } = useDashboard({
    dateRange,
    isLeadsModalOpen: showLeads,
    isCallsModalOpen: showCalls,
    isEmailsModalOpen: showEmailOpens,
    isWebsiteModalOpen: showWebsiteStats,
    isEmailEngagementModalOpen: !!showEmailEngagement,
    emailEngagementEventType: showEmailEngagement ?? undefined,
    webEventTypes,
    sortField: leadsSortField,
    sortDirection: leadsSortDirection,
    leadsPageSize,
    leadsLeadSources: leadsSourceFilter.length ? leadsSourceFilter : undefined,
    leadsSearch: leadsSearchDebounced || undefined,
    webDeviceType: webDeviceFilter === 'all' ? undefined : webDeviceFilter,
    webIsGuest: webVisitorFilter === 'all' ? undefined : webVisitorFilter === 'guest',
    webSearch: webSearchDebounced || undefined,
  });

  const { stats: handoffStats, isLoading: handoffLoading } = useHandoffStats({
    allTime: true,
    enabled: flywheelEnabled,
  });

  const {
    transfers: liveTransfers,
    total: liveTransfersTotal,
    hasMore: liveTransfersHasMore,
    isLoading: liveTransfersLoading,
    loadMore: liveTransfersLoadMore,
    loadPage: liveTransfersLoadPage,
    filter: liveTransfersFilter,
  } = useOrgLiveTransfersGQL({ dateRange, enabled: showLiveTransfers });

  // SMS drill-down — shows one row per conversation (SPEC-1880). The
  // useConversationThreadsGQL hook backs the modal; legacy useOrgSmsMessagesGQL
  // is kept around for the per-thread "open conversation" drill-down view.
  const smsThreadsGQL = useConversationThreadsGQL({
    channel: 'sms',
    enabled: showSms,
    // SPEC-1906/1907: backend filters threads pre-grouping by message
    // created_at; the row's latest-message metadata reflects the newest
    // match within the window. Date picker is no longer a no-op.
    dateRange,
  });

  const conversationsGQL = useOrgCallsGQL({
    dateRange,
    answeredBy: 'human',
    enabled: showConversations,
  });

  const opportunityCallsGQL = useOrgCallsGQL({
    dateRange,
    outcomes: ['nurture', 'follow_up'],
    enabled: showOpportunityCalls,
  });

  // orgStats sourced from useDashboard (GET_ORGANIZATION_STATS already runs inside useDashboardStatsGQL)
  const orgStats = dashboardData?.stats.orgStats ?? null;

  // Follow-up stats for Flywheel widget — all-time
  const SECONDS_PER_FOLLOWUP = 45; // Compatibility fallback for older API revisions.
  const { data: followUpStatsData, isLoading: followUpLoading } = useFollowUpStats({
    allTime: true,
    enabled: flywheelEnabled,
  });
  const followUpData: FollowUpData | null = followUpStatsData
    ? {
        total: followUpStatsData.summary.totalFollowUps,
        uniqueContacts: followUpStatsData.summary.uniqueContacts,
        outboundSms: followUpStatsData.summary.outboundSms ?? 0,
        calls: sumFollowUpTypes(followUpStatsData.byChannel, [
          'voice',
          'call',
          'schedule_call',
          'schedule_voice',
        ]),
        texts: sumFollowUpTypes(followUpStatsData.byChannel, [
          'sms',
          'text',
          'send_sms',
          'schedule_sms',
          'send_text',
          'schedule_text',
        ]),
        emails: sumFollowUpTypes(followUpStatsData.byChannel, [
          'email',
          'send_email',
          'schedule_email',
        ]),
        timeSaved: formatCallDuration(
          followUpStatsData.summary.totalTimeSavedSeconds ??
            followUpStatsData.summary.totalFollowUps * SECONDS_PER_FOLLOWUP
        ),
        types: getFollowUpTypeBreakdown(followUpStatsData.byChannel),
        byStrategy: followUpStatsData.byStrategy ?? EMPTY_FOLLOW_UP_STRATEGY,
      }
    : null;

  const isDataLoading = isLoading;

  const handleOpenEditModal = (contact: { id: string }) => {
    setSelectedContact({ id: contact.id });
    setShowEditModal(true);
  };

  // Widget definitions
  const widgetMap = buildWidgetMap({
    dashboardData,
    isDataLoading,
    flywheelEnabled,
    opportunities: dashboardData?.stats.opportunities?.value || 0,
    conversations: callsGQL.conversationsTotal,
    handoffStats,
    handoffLoading,
    webActivitiesTotal: webActivitiesGQL.total,
    webActivitiesLoading: isLoading,
    propertyViewsTotal: dashboardData?.stats.propertyViewsTotal ?? 0,
    leadCapturesTotal: dashboardData?.stats.leadCapturesTotal ?? 0,
    emailDeliveredTotal: dashboardData?.stats.emailDeliveredTotal ?? 0,
    emailOpensTotal: dashboardData?.stats.emailOpensTotal ?? 0,
    emailClicksTotal: dashboardData?.stats.emailClicksTotal ?? 0,
    smsTotal: dashboardData?.stats.orgStats?.totalSms ?? 0,
    smsLoading: isDataLoading,
    followUpData,
    followUpLoading,
    orgStats,
    orgStatsLoading: isLoading,
    liveTransfersTotal: dashboardData?.stats.liveTransfers ?? liveTransfersTotal,
    liveTransfersLoading: isLoading,
    opportunityCallsTotal: dashboardData?.stats.opportunityCallsTotal ?? 0,
    opportunityCallsLoading: isLoading,
    onShowOpportunityCalls: () => setShowOpportunityCalls(true),
    onShowLeads: () => {
      setLeadsSourceFilter(ALL_LEAD_SOURCES);
      setShowLeads(true);
      if (refreshLeads) refreshLeads();
    },
    onShowLeadsBySource: (source: string) => {
      setLeadsSourceFilter([source]);
      setShowLeads(true);
      if (refreshLeads) refreshLeads();
    },
    onShowCalls: () => {
      callsGQL.refresh();
      setShowCalls(true);
    },
    onShowConversations: () => setShowConversations(true),
    onShowOpportunities: () => setShowOpportunities(true),
    onShowHandoffs: () => setHandoffStatus('pending'),
    onShowClaimedHandoffs: () => setHandoffStatus('claimed'),
    onShowReturnedHandoffs: () => setHandoffStatus('released'),
    onShowDeclinedHandoffs: () => setHandoffStatus('declined'),
    onShowExpiredHandoffs: () => setHandoffStatus('expired'),
    onShowLiveTransfers: () => setShowLiveTransfers(true),
    onShowEmailOpens: () => {
      emailsGQL.resetPage();
      setShowEmailOpens(true);
    },
    onShowEmailEngagement: (eventType) => {
      emailEngagementGQL.goToPage(0);
      setShowEmailEngagement(eventType);
    },
    onShowSms: () => {
      smsThreadsGQL.goToPage(0);
      setShowSms(true);
    },
    onShowWebsiteStats: () => {
      webActivitiesGQL.goToPage(0);
      setShowWebsiteStats(true);
    },
    onShowPropertyViews: () => {
      setWebEventTypeFilter('property_view');
      webActivitiesGQL.goToPage(0);
      setShowWebsiteStats(true);
    },
    onShowLeadCaptures: () => {
      setWebEventTypeFilter('lead_capture');
      webActivitiesGQL.goToPage(0);
      setShowWebsiteStats(true);
    },
    onShowPageViews: () => setShowPageViews(true),
    // Active Users / Nurture / Search Alerts widgets are not currently
    // surfaced — widgetDefinitions still requires the handlers, so we
    // pass no-ops until those widgets are re-introduced.
    onShowActiveUsers: () => {},
    onShowNurture: () => {},
    onShowSearchAlerts: () => {},
    onShowFlywheelFollowUps: setFlywheelDrilldown,
  });

  const pinnedWidgetMap = buildPinnedWidgetMap(pinnedWidgets);

  // All known widget IDs (core + pinned) that actually exist
  const allWidgetMap = useMemo(
    () => ({ ...widgetMap, ...pinnedWidgetMap }),
    [widgetMap, pinnedWidgetMap]
  );
  const validIds = useMemo(() => {
    const ids = new Set(Object.keys(allWidgetMap));
    ids.add('live_transfers');
    ids.add('sms');
    // Appointments hidden until the API exposes a live aggregate.
    ids.delete('appointments');
    if (flywheelEnabled) {
      ids.add('flywheel');
      ids.add('handoffs');
      ids.delete('opportunities');
    } else {
      ids.delete('flywheel');
      ids.delete('handoffs');
      ids.delete('opportunities');
    }
    return ids;
  }, [flywheelEnabled, allWidgetMap]);

  // Unified order: only show what's explicitly in widgetOrder (and still valid)
  const allOrder = useMemo(() => {
    let filtered = widgetOrder.filter((id) => validIds.has(id));
    const hasUserWidget = filtered.some((id) => id !== 'live_transfers');
    if (!hasUserWidget && !isCustomized) {
      return getDefaultOrder(flywheelEnabled);
    }
    // Ensure flywheel locked widgets are always present when flywheel is enabled,
    // even if the saved order was from a non-flywheel session.
    if (flywheelEnabled) {
      const missing = ['flywheel', 'handoffs'].filter((id) => !filtered.includes(id));
      if (missing.length) filtered = [...missing, ...filtered];
    }
    return filtered;
  }, [widgetOrder, validIds, flywheelEnabled, isCustomized]);

  const allVisibleWidgets = allOrder.map((id) => allWidgetMap[id]).filter(Boolean);

  // Seed from the displayed order so adding a widget never drops existing defaults
  const handleAddWidget = useCallback(
    (id: string) => {
      if (allOrder.includes(id)) return;
      setWidgetOrder([...allOrder, id]);
    },
    [allOrder, setWidgetOrder]
  );

  // All valid core widgets for the library modal
  const coreWidgets = ALL_WIDGETS.filter((w) => validIds.has(w.id));

  // All Remi-pinned widgets for the library modal
  const allLibraryWidgets: AvailableWidget[] = pinnedWidgets.map((pw) => ({
    id: pw.id,
    label: pw.block.title,
    description:
      [pw.block.source, pw.block.timeframe].filter(Boolean).join(' · ') || 'Generated by Remi',
    icon: <Zap className="w-4 h-4" />,
  }));

  // IDs currently on the dashboard
  const activeWidgetIds = new Set(allOrder);

  const sensors = useSensors(
    useSensor(SmartPointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = allOrder.indexOf(active.id as string);
      const newIndex = allOrder.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      setWidgetOrder(arrayMove(allOrder, oldIndex, newIndex));
    },
    [allOrder, setWidgetOrder]
  );

  const { users: orgUsers } = useOrganizationUsers();
  const leadsColumns = getLeadsColumns(handleOpenEditModal, timezone, orgUsers);

  return (
    <div className="space-y-4 2xl:space-y-6 pb-6 2xl:pb-8">
      <DateRangeBar
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        timezone={timezone}
        liveStats={{
          calls: dashboardData?.stats.calls || 0,
          emails: dashboardData?.stats.emailDeliveredTotal || 0,
          leads: dashboardData?.stats.leads.value || 0,
          conversations: callsGQL.conversationsTotal,
          followUps: followUpData?.total || 0,
          webInteractions: webActivitiesGQL.total,
        }}
      />

      <div className="space-y-3">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={allOrder} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 2xl:gap-4">
              {allVisibleWidgets.map(({ card }) => {
                const size = getWidgetSize(card.id);
                const canRemove = allVisibleWidgets.length > 1;
                return (
                  <SortableStatCard
                    key={card.id}
                    card={card}
                    colSpan={size}
                    onResize={(size) => setWidgetSize(card.id, size)}
                    highlighted={isHighlighted(card.id)}
                    onToggleHighlight={() => toggleHighlight(card.id)}
                    onRemove={canRemove ? () => removeWidget(card.id) : undefined}
                  />
                );
              })}
              {/* Show "Add Widget" only at lg (4-col) when there's a spare slot in the row */}
              {allVisibleWidgets.reduce((sum, { card }) => sum + getWidgetSize(card.id), 0) % 4 !==
                0 && (
                <button
                  onClick={() => setShowLibrary(true)}
                  className="hidden lg:flex rounded-xl min-h-[120px] 2xl:min-h-[140px] h-full border border-dashed border-foreground/30 hover:border-foreground/50 bg-foreground/[0.02] hover:bg-foreground/[0.04] flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground transition-all duration-200 cursor-pointer group"
                >
                  <div className="p-2 rounded-full border border-foreground/30 group-hover:border-foreground/50 transition-colors">
                    <Plus className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-medium">Add Widget</span>
                </button>
              )}
            </div>
          </SortableContext>
        </DndContext>
        <div className="flex items-center gap-2">
          <AddWidgetCard
            coreWidgets={coreWidgets}
            libraryWidgets={allLibraryWidgets}
            activeWidgetIds={activeWidgetIds}
            onAdd={handleAddWidget}
            onRemoveFromDashboard={removeWidget}
            onPermanentDelete={unpinWidget}
            isOpen={showLibrary}
            onOpenChange={setShowLibrary}
          />

          {isSyncing && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground opacity-80">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              Syncing preferences
            </span>
          )}
        </div>
      </div>

      <LeadActivity onContactClick={(id) => handleOpenEditModal({ id })} />

      {/* Leads Modal */}
      {showLeads && (
        <StatsModal
          isOpen={showLeads}
          onClose={() => {
            setShowLeads(false);
            setLeadsSourceFilter(ALL_LEAD_SOURCES);
            setLeadsSearchDebounced('');
            if (leadsSearchTimerRef.current) clearTimeout(leadsSearchTimerRef.current);
          }}
          title="Leads"
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          onSearch={handleLeadsSearch}
          searchPlaceholder="Search leads by name, phone, email..."
          activeFilterCount={
            leadsSourceFilter.length && leadsSourceFilter.length !== ALL_LEAD_SOURCES.length ? 1 : 0
          }
          currentPage={leadsCurrentPage}
          pageSize={leadsPageSize}
          onPageChange={goToLeadsPage}
          totalCount={totalLeadsCount || dashboardData?.stats.leads.value || 0}
          onPageSizeChange={(size) => {
            setLeadsPageSize(size);
            goToLeadsPage(0);
          }}
          onSort={(field, direction) => {
            setLeadsSortField(field);
            setLeadsSortDirection(direction);
            setTimeout(() => refreshLeads(), 100);
          }}
          customFilters={
            <Select
              value={leadsSourceFilter}
              onChange={(v) => {
                const selected = Array.isArray(v) ? v : [v];
                setLeadsSourceFilter(selected.length ? selected : ALL_LEAD_SOURCES);
                goToLeadsPage(0);
              }}
              options={[
                { value: 'Email', label: 'Email' },
                { value: 'PPC', label: 'PPC' },
                { value: 'Organic', label: 'Organic' },
              ]}
              multiple
              className="w-[300px]"
            />
          }
          columns={leadsColumns}
          data={dashboardData?.leads || []}
          isLoading={isLoading}
        />
      )}

      {/* Opportunities Modal */}
      {showOpportunities && (
        <CallStatsModal
          isOpen={showOpportunities}
          onClose={() => setShowOpportunities(false)}
          dateRange={dateRange}
          orgTimezone={timezone}
          type="opportunities"
          callsMade={dashboardData?.stats.calls || 0}
          conversations={dashboardData?.stats.calls || 0}
          opportunities={dashboardData?.stats.opportunityCallsTotal || 0}
        />
      )}

      {/* Calls Modal */}
      {showCalls && (
        <CallStatsModal
          isOpen={showCalls}
          onClose={() => setShowCalls(false)}
          dateRange={dateRange}
          orgTimezone={timezone}
          type="all"
          callsMade={callsGQL.total}
          conversations={0}
          opportunities={0}
          gqlCalls={callsGQL.calls as GQLCallSummary[]}
          gqlTotal={callsGQL.total}
          gqlHasMore={callsGQL.hasMore}
          gqlLoadMore={callsGQL.loadMore}
          gqlLoadPage={callsGQL.loadPage}
          gqlSort={callsGQL.sort}
          gqlFilter={callsGQL.filter}
          gqlIsLoading={callsGQL.isLoading}
        />
      )}

      {/* Conversations Modal */}
      {showConversations && (
        <CallStatsModal
          isOpen={showConversations}
          onClose={() => setShowConversations(false)}
          dateRange={dateRange}
          orgTimezone={timezone}
          type="answered"
          initialAnsweredBy="human"
          callsMade={callsGQL.conversationsTotal}
          conversations={callsGQL.conversationsTotal}
          opportunities={0}
          gqlCalls={conversationsGQL.calls as GQLCallSummary[]}
          gqlTotal={conversationsGQL.total}
          gqlHasMore={conversationsGQL.hasMore}
          gqlLoadMore={conversationsGQL.loadMore}
          gqlLoadPage={conversationsGQL.loadPage}
          gqlSort={conversationsGQL.sort}
          gqlFilter={conversationsGQL.filter}
          gqlIsLoading={conversationsGQL.isLoading}
        />
      )}

      {/* Handoffs Modal — single render driven by `handoffStatus`.
          Each tile's onClick sets the status; close resets to null. */}
      {handoffStatus && (
        <HandoffsStatsModal
          isOpen
          onClose={() => setHandoffStatus(null)}
          dateRange={dateRange}
          orgTimezone={timezone}
          handoffCount={handoffStats[handoffStatus] ?? 0}
          status={handoffStatus}
        />
      )}

      {/* Live Transfers Modal */}
      {showLiveTransfers && (
        <LiveTransfersModal
          isOpen={showLiveTransfers}
          onClose={() => setShowLiveTransfers(false)}
          dateRange={dateRange}
          orgTimezone={timezone}
          transferCount={liveTransfersTotal}
          transfers={liveTransfers}
          total={liveTransfersTotal}
          hasMore={liveTransfersHasMore}
          isLoading={liveTransfersLoading}
          loadMore={liveTransfersLoadMore}
          loadPage={liveTransfersLoadPage}
          gqlFilter={liveTransfersFilter}
          onContactClick={(id) => handleOpenEditModal({ id })}
        />
      )}

      {/* Email Opens / SMS / Email Engagement — extracted to their own
          files. Each takes the parent's GQL query result and the contact
          opener; the StatsModal chrome lives inside each component. */}
      <EmailsStatsModal
        isOpen={showEmailOpens}
        onClose={() => setShowEmailOpens(false)}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        timezone={timezone}
        emailsGQL={emailsGQL}
        onOpenContact={(id) => handleOpenEditModal({ id })}
      />

      <SmsStatsModal
        isOpen={showSms}
        onClose={() => setShowSms(false)}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        timezone={timezone}
        smsThreadsGQL={smsThreadsGQL}
        onOpenContact={(id) => handleOpenEditModal({ id })}
        onOpenThread={(selection) => setSelectedSmsThread(selection)}
      />

      <EmailEngagementStatsModal
        kind={showEmailEngagement}
        onClose={() => setShowEmailEngagement(null)}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        timezone={timezone}
        emailEngagementGQL={emailEngagementGQL}
        onOpenContact={(id) => handleOpenEditModal({ id })}
      />

      {/* Website Interactions Modal — extracted to its own file so the
          page component stays focused on state wiring rather than
          column renderers. */}
      <WebActivityStatsModal
        isOpen={showWebsiteStats}
        onClose={() => setShowWebsiteStats(false)}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        timezone={timezone}
        webActivitiesGQL={webActivitiesGQL}
        webEventTypeFilter={webEventTypeFilter}
        setWebEventTypeFilter={setWebEventTypeFilter}
        webDeviceFilter={webDeviceFilter}
        setWebDeviceFilter={setWebDeviceFilter}
        webVisitorFilter={webVisitorFilter}
        setWebVisitorFilter={setWebVisitorFilter}
        onSearch={handleWebSearch}
        webSearchTimerRef={webSearchTimerRef}
        resetSearch={() => setWebSearchDebounced('')}
        onOpenContact={(id) => handleOpenEditModal({ id })}
      />

      {/* GQL stat detail modals */}
      {showPageViews && (
        <StatsModal
          isOpen={showPageViews}
          onClose={() => setShowPageViews(false)}
          title="Page Views"
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          currentPage={0}
          pageSize={5}
          totalCount={5}
          isLoading={isLoading}
          columns={[
            { key: 'metric', header: 'Metric', width: '250px' },
            {
              key: 'value',
              header: 'Count',
              width: '120px',
              cell: (item: Record<string, unknown>) => formatNumber(item.value as number),
            },
          ]}
          data={[
            { metric: 'Property Views', value: orgStats?.totalPropertyViews || 0 },
            { metric: 'Search Results', value: orgStats?.totalSearchPageViews || 0 },
          ]}
        />
      )}

      {/* Opportunity Calls Modal */}
      {showOpportunityCalls && (
        <CallStatsModal
          isOpen={showOpportunityCalls}
          onClose={() => setShowOpportunityCalls(false)}
          dateRange={dateRange}
          orgTimezone={timezone}
          type="opportunities"
          callsMade={opportunityCallsGQL.total}
          conversations={0}
          opportunities={opportunityCallsGQL.total}
          gqlCalls={opportunityCallsGQL.calls as GQLCallSummary[]}
          gqlTotal={opportunityCallsGQL.total}
          gqlHasMore={opportunityCallsGQL.hasMore}
          gqlLoadMore={opportunityCallsGQL.loadMore}
          gqlLoadPage={opportunityCallsGQL.loadPage}
          gqlSort={opportunityCallsGQL.sort}
          gqlFilter={opportunityCallsGQL.filter}
          gqlIsLoading={opportunityCallsGQL.isLoading}
        />
      )}

      {flywheelDrilldown?.kind === 'followups' && (
        <FlywheelFollowUpsModal
          isOpen
          filter={flywheelDrilldown}
          timezone={timezone}
          onClose={() => setFlywheelDrilldown(null)}
          onOpenContact={(id) => handleOpenEditModal({ id })}
        />
      )}

      {flywheelDrilldown?.kind === 'contacts' && (
        <FlywheelContactsModal
          isOpen
          filter={flywheelDrilldown}
          onClose={() => setFlywheelDrilldown(null)}
          onOpenContact={(id) => handleOpenEditModal({ id })}
        />
      )}

      <EditContactModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedContact(null);
        }}
        contact={selectedContact}
        onSubmit={async () => {
          setShowEditModal(false);
          setSelectedContact(null);
        }}
        onSuccess={() => {
          if (showLeads && refreshLeads) refreshLeads();
        }}
      />

      <SmsThreadModal
        isOpen={!!selectedSmsThread}
        onClose={() => setSelectedSmsThread(null)}
        contactId={selectedSmsThread?.contactId ?? null}
        contactName={selectedSmsThread?.contactName ?? null}
        contactPhone={selectedSmsThread?.contactPhone ?? null}
        totalCount={selectedSmsThread?.totalCount ?? null}
        anchor={selectedSmsThread?.anchor ?? null}
      />
    </div>
  );
};
